import React, { useEffect, useState } from 'react';
import { Button, Modal, Table, Tag, Form, Input, InputNumber, Popconfirm, message, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { staffAPI } from '../services/api';
import StudioNavBar from '../components/common/StudioNavBar';
import { useAuth } from '../context/AuthContext';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE } from '../constants/localeOptions';
import { formatCurrencySimple } from '../utils/currency';

const roleColors: Record<string, string> = {
  Staff: 'blue',
  staff: 'cyan',
  engineer: 'purple',
  admin: 'gold',
};

const Staff: React.FC = () => {
  const { isStaff } = useAuth();
  const [staffs, setStaffs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [viewingStaff, setViewingStaff] = useState<any | null>(null);
  const [editingStaff, setEditingStaff] = useState<any | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [form] = Form.useForm();

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleView = (Staff: any) => {
    setViewingStaff(Staff);
  };

  const handleEdit = (Staff: any) => {
    setEditingStaff(Staff);
    setTimeout(() => {
      form.setFieldsValue(Staff);
    }, 0);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await staffAPI.deleteStaff(id);
      message.success('Staff deleted successfully');
      fetchStaffs();
    } catch (err) {
      message.error('Failed to delete Staff');
    }
  };

  const responsiveSm: Breakpoint[] = ['sm'];
  const responsiveMd: Breakpoint[] = ['md'];
  const responsiveLg: Breakpoint[] = ['lg'];

  const columns: ColumnsType<any> = [
    { 
      title: 'ID', 
      dataIndex: 'custom_id', 
      key: 'custom_id', 
      sorter: true, 
      width: 100,
      render: (_: any, record: any) => record.custom_id || record.id 
    },
    { 
      title: 'Name', 
      dataIndex: 'first_name', 
      key: 'first_name', 
      sorter: true,
      width: 150,
      render: (_: any, record: any) => `${record.first_name || ''} ${record.last_name || ''}`,
      ellipsis: true
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email',
      width: 150,
      responsive: responsiveSm,
      ellipsis: true
    },
    { 
      title: 'Phone', 
      dataIndex: 'phone', 
      key: 'phone',
      width: 130,
      responsive: responsiveMd,
      ellipsis: true
    },
    { 
      title: 'Role', 
      dataIndex: 'role', 
      key: 'role',
      width: 110,
      render: (role: string, record: any) => <Tag color={roleColors[role] || 'default'}>{role === 'staff' && record.staff_title ? record.staff_title : (role === 'producer' ? 'Producer' : 'Staff')}</Tag>,
      filters: [
        { text: 'Staff', value: 'Staff' },
        { text: 'Engineer', value: 'engineer' },
        { text: 'Admin', value: 'admin' },
      ] 
    },
    { 
      title: 'Compensation', 
      key: 'compensation',
      width: 150,
      responsive: responsiveLg,
      render: (_: any, record: any) => {
        if (record.role === 'staff') {
          return formatCurrencySimple(record.salary || 0, record.salary_currency || DEFAULT_CURRENCY_CODE);
        } else {
          const num = Number(record.commission_rate);
          return !isNaN(num) ? `${num}%` : '0%';
        }
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <div className="flex gap-1">
          {isStaff ? (
            // Staffs can only view
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => handleView(record)}
              size="small"
            >
              View
            </Button>
          ) : (
            // Studio owners can edit and delete
            <>
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => handleEdit(record)}
                size="small"
                className="hidden sm:inline-flex"
              >
                Edit
              </Button>
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => handleEdit(record)}
                size="small"
                className="sm:hidden"
              />
              <Popconfirm
                title="Delete Staff?"
                description="Are you sure you want to delete this Staff?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Popconfirm>
            </>
          )}
        </div>
      )
    },
  ];

  useEffect(() => {
    fetchStaffs();
  }, []);

  const fetchStaffs = async () => {
    setLoading(true);
    try {
      const res = await staffAPI.getStaff();
      setStaffs(res.data);
    } catch (err) {
      message.error('Failed to fetch Staffs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingStaff(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleSave = async (values: any) => {
    try {
      // Check for duplicate custom_id if provided
      if (values.custom_id) {
        const duplicateExists = staffs.some((p: any) => 
          p.custom_id === values.custom_id && 
          (!editingStaff || p.id !== editingStaff.id)
        );
        
        if (duplicateExists) {
          message.error(`Staff with ID "${values.custom_id}" already exists in your studio`);
          return;
        }
      }
      
      if (editingStaff) {
        await staffAPI.updateStaff(editingStaff.id, values);
      } else {
        await staffAPI.createStaff(values);
      }
      setShowModal(false);
      form.resetFields();
      message.success('Staff saved successfully');
      fetchStaffs();
    } catch (err: any) {
      console.error('Save error:', err);
      
      // Handle backend validation error for duplicate custom_id
      if (err.response?.data?.custom_id) {
        message.error(err.response.data.custom_id[0]);
      } else {
        message.error('Failed to save Staff');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <StudioNavBar />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Staff</h1>
            <p className="text-xs sm:text-sm text-slate-500">Manage your production team and commission rates.</p>
          </div>
          {!isStaff && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAdd} 
              size="large"
              className="w-full sm:w-auto"
            >
              Add Staff
            </Button>
          )}
        </div>
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <Table
            columns={columns}
            dataSource={staffs}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </main>
      <Modal
        title={editingStaff ? 'Edit Staff' : 'Add Staff'}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingStaff ? 'Update' : 'Create'}
        width={isMobile ? '95vw' : 600}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          preserve={false}
        >
          <Form.Item name="custom_id" label="Staff ID">
            <Input placeholder="Enter Staff ID" size="large" />
          </Form.Item>
          <Form.Item name="first_name" label="First Name" rules={[{ required: true, message: 'Please enter first name' }]}>
            <Input placeholder="Enter first name" size="large" />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name" rules={[{ required: true, message: 'Please enter last name' }]}>
            <Input placeholder="Enter last name" size="large" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}>
            <Input placeholder="Enter email" size="large" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="Enter phone number" size="large" />
          </Form.Item>
          <Form.Item 
            name="role" 
            label="Role / Designation" 
            rules={[{ required: true, message: 'Please select a role' }]}
            initialValue="Staff"
          >
            <Select size="large">
              <Select.Option value="producer">Producer</Select.Option>
              <Select.Option value="staff">Staff</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}>
            {({ getFieldValue }) =>
              getFieldValue('role') === 'staff' ? (
                <>
                  <Form.Item name="staff_title" label="Staff Title" rules={[{ required: true, message: 'Please enter staff title' }]}>
                    <Input placeholder="Enter title (e.g. Mixing Engineer, Assistant)" size="large" />
                  </Form.Item>
                  <div className="flex gap-4">
                    <Form.Item name="salary" label="Monthly Salary" className="flex-1" rules={[{ required: true, message: 'Please enter salary amount' }]}>
                      <InputNumber style={{ width: '100%' }} min={0} placeholder="Enter monthly salary" size="large" />
                    </Form.Item>
                    <Form.Item name="salary_currency" label="Currency" initialValue={DEFAULT_CURRENCY_CODE} className="w-32">
                      <Select size="large" showSearch optionFilterProp="children">
                        {CURRENCY_OPTIONS.map(opt => (
                          <Select.Option key={opt.value} value={opt.value}>{opt.shortLabel}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </>
              ) : (
                <Form.Item name="commission_rate" label="Commission Rate (%)">
                  <InputNumber style={{ width: '100%' }} suffix="%" min={0} max={100} placeholder="Enter commission rate" size="large" />
                </Form.Item>
              )
            }
          </Form.Item>
        </Form>
      </Modal>

      {/* View Staff Modal (for Staffs - read-only) */}
      <Modal
        title="Staff Details"
        open={!!viewingStaff}
        onCancel={() => setViewingStaff(null)}
        footer={[
          <Button key="close" onClick={() => setViewingStaff(null)}>
            Close
          </Button>
        ]}
        width={isMobile ? '95vw' : 500}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        {viewingStaff && (
          <div className="space-y-3">
            <div><strong>Staff ID:</strong> {viewingStaff.custom_id || viewingStaff.id}</div>
            <div><strong>Name:</strong> {viewingStaff.first_name} {viewingStaff.last_name}</div>
            <div><strong>Email:</strong> {viewingStaff.email}</div>
            <div><strong>Phone:</strong> {viewingStaff.phone || 'N/A'}</div>
            <div><strong>Role:</strong> <Tag color={roleColors[viewingStaff.role] || 'default'}>{viewingStaff.role === 'staff' && viewingStaff.staff_title ? viewingStaff.staff_title : (viewingStaff.role === 'producer' ? 'Producer' : 'Staff')}</Tag></div>
            {viewingStaff.role === 'staff' ? (
              <div><strong>Salary:</strong> {formatCurrencySimple(viewingStaff.salary || 0, viewingStaff.salary_currency || DEFAULT_CURRENCY_CODE)} / mo</div>
            ) : (
              <div><strong>Commission Rate:</strong> {viewingStaff.commission_rate || 0}%</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Staff;
